import { getJWTToken } from '../../utils/auth';
import { authHeaders, authHeadersFor, type ImpersonationTarget } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type ReportStudent = {
  key: string;
  listKey?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  grade: string;
  school: string;
  district: string;
  homeLanguage: string;
  teacher: string;
  class?: string;
};

export type Level = { score: number; label: string; color: string };
export type Band = { min: number; max: number; label: string; color: string };

export type WindowRef = {
  window: string;
  schoolYear: number;
  date: string;
  label: string;
  legend: string;
  month: string;
};

export type ShownIndicator = { name: string; scores: (number | null)[] };

export type ShownDomain = { domain: string; overall: (number | null)[]; indicators: ShownIndicator[] };

export type HistoryPoint = { label: string; date: string; speaking: number | null; writing: number | null };

export type ReportBenchmarks = {
  hasData: boolean;
  top: number;
  scheme: Level[];
  bands: Band[];
  shown: WindowRef[];
  domains: ShownDomain[];
  narrative: string[];
  history: HistoryPoint[];
};

export type AccessLevels = {
  label: string;
  listening: number | null;
  speaking: number | null;
  reading: number | null;
  writing: number | null;
  overall: number | null;
};

export type ReportAccess = AccessLevels & { scale: string; prior: AccessLevels | null };

export type WritingSample = { label: string; prompt: string; wordCount: number; text: string; shortened: boolean };
export type SpeakingSample = {
  label: string;
  prompt: string;
  durationSec: number | null;
  transcript: string | null;
  wordCount: number | null;
  shortened: boolean;
  listenUrl?: string;
};

export type NextStep = { heading: string; definition: string };
export type NextSteps = { label: string; speaking: NextStep | null; writing: NextStep | null };
export type FeedbackRun = { text: string; bold?: boolean; italic?: boolean; underline?: boolean };
export type Feedback = { label: string; paragraphs: FeedbackRun[][] };

export type ProgressReport = {
  preparedOn: string;
  schoolYear: string;
  student: ReportStudent;
  contact: { teacher: string; school: string };
  interpreter: { language: string; text: string; rtl: boolean } | null;
  benchmarks: ReportBenchmarks;
  access: ReportAccess | null;
  samples: { writing: WritingSample[]; speaking: SpeakingSample[] };
  nextSteps: NextSteps | null;
  feedback: Feedback | null;
};

export class ProgressReportFetchError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ProgressReportFetchError';
    this.status = status;
  }
}

export type ReportAuth = { token: string | undefined; target: ImpersonationTarget | null };

export async function fetchProgressReport(studentId: string, auth?: ReportAuth): Promise<ProgressReport> {
  const token = auth ? auth.token : await getJWTToken();
  if (token === undefined) throw new ProgressReportFetchError('no session token', 0);
  const response = await fetch(
    `${apiUrl}/students/${encodeURIComponent(studentId)}/progress-report`,
    { headers: auth ? authHeadersFor(token, auth.target) : authHeaders(token) },
  );
  if (!response.ok) {
    throw new ProgressReportFetchError(`progress-report fetch failed: ${response.status}`, response.status);
  }
  const report = (await response.json()) as ProgressReport;
  return { ...report, nextSteps: report.nextSteps ?? null, feedback: report.feedback ?? null };
}

export const HATCH_FILL = '#C1C1CD';

export const STRINGS = {
  title: 'Student Language Learning Progress Report',
  page: (n: number, of: number) => `Page ${n} of ${of}`,
  teacher: (name: string) => `Teacher: ${name}`,
  prepared: (date: string) => `Prepared ${date}`,
  homeLanguage: (language: string) => `Home language: ${language}`,
  growthTitle: 'Benchmark growth history',
  growthSubtitle: (n: number) => `Overall scores (most recent ${n} benchmarks)`,
  speaking: 'Speaking',
  writing: 'Writing',
  skillsTitle: (n: number) => (n >= 2 ? `Indicator scores (most recent ${n} benchmarks)` : 'Benchmark scores'),
  domainOverall: (domain: string) => `${domainTitle(domain)} Overall`,
  domainIndicators: (domain: string) => `${domainTitle(domain)} Indicators`,
  proficiencyLevels: 'Proficiency Levels',
  accessTitle: (test: string) => `Most recent ${test} test results`,
  accessScale: (test: string, max: number) => `${test} is scored on a scale of 1–${max}`,
  accessDomains: { listening: 'Listening', speaking: 'Speaking', reading: 'Reading', writing: 'Writing', overall: 'Overall' },
  writingTitle: (first: string) => `${first}'s benchmark writing`,
  speakingTitle: (first: string) => `${first}'s benchmark speaking`,
  nextStepsTitle: (first: string) => `${first}'s next steps`,
  feedbackTitle: (first: string) => `${first}'s benchmark feedback`,
  benchmark: (label: string) => `${label} benchmark`,
  benchmarkContinued: (label: string) => `${label} benchmark · continued`,
  continuesOn: (page: number) => `Continued on page ${page} →`,
  task: (prompt: string) => `The task: ${prompt}`,
  wordUnit: (n: number) => (n === 1 ? 'word' : 'words'),
  wordsIn: (n: number, duration: string) => `${n === 1 ? 'word' : 'words'} in ${duration}`,
  ofSpeaking: 'of speaking',
  noTranscription: 'No transcription available for this recording.',
  nothingYet: (first: string) => `No Flashlight Benchmark or ACCESS results are on file for ${first} yet.`,
  listenTitle: 'Listen to this recording',
  listenHint: (first: string) => `Scan the code with a phone camera to hear ${first}.`,
  listenAria: (first: string, label: string) => `QR code that plays ${first}'s ${label} recording`,
  shortened: 'Shortened to fit this page. The full response is in Flashlight360.',
  student: 'Student',
  theStudent: 'the student',
} as const;

export function levelOf(bands: Band[], value: number | null): number {
  if (value == null || bands.length === 0) return 0;
  const rounded = Math.round(value * 10) / 10;
  const index = bands.findIndex((band) => rounded >= band.min && rounded <= band.max);
  if (index >= 0) return index + 1;
  return rounded > bands[bands.length - 1]!.max ? bands.length : 0;
}

export function bandFor(bands: Band[], value: number | null): Band | null {
  if (value == null) return null;
  const rounded = Math.round(value * 10) / 10;
  return bands.find((band) => rounded >= band.min && rounded <= band.max) ?? bands[bands.length - 1] ?? null;
}

export function colorFor(bands: Band[], value: number | null): string {
  return bandFor(bands, value)?.color || HATCH_FILL;
}

export function formatLevel(value: number | null): string {
  if (value == null) return '—';
  return (Math.round(value * 10) / 10).toFixed(1);
}

export function formatRange(band: Band): string {
  const min = Number.isInteger(band.min) ? String(band.min) : band.min.toFixed(1);
  const max = Number.isInteger(band.max) ? String(band.max) : band.max.toFixed(1);
  return `${min}-${max}`;
}

export function formatDuration(seconds: number | null): string | null {
  if (seconds == null) return null;
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

export function writingCaption(sample: WritingSample): string {
  return STRINGS.benchmark(sample.label);
}

export function speakingCaption(sample: SpeakingSample): string {
  return STRINGS.benchmark(sample.label);
}

// The big number at the top of a sample, and the words that follow it.
export type SampleLength = { value: string; unit: string };

export function writingLength(sample: WritingSample): SampleLength {
  return { value: String(sample.wordCount), unit: STRINGS.wordUnit(sample.wordCount) };
}

// Words spoken only count when there is a transcript to count them from;
// without one the recording's length stands alone.
export function speakingLength(sample: SpeakingSample): SampleLength | null {
  const duration = formatDuration(sample.durationSec);
  const words = sample.transcript != null ? sample.wordCount : null;
  if (words != null) {
    return { value: String(words), unit: duration ? STRINGS.wordsIn(words, duration) : STRINGS.wordUnit(words) };
  }
  return duration ? { value: duration, unit: STRINGS.ofSpeaking } : null;
}

export function paragraphsOf(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

export function previewSubline(report: ProgressReport, pages: number | null): string {
  const current = report.benchmarks.shown[report.benchmarks.shown.length - 1];
  return [
    gradeLabel(report.student.grade),
    (report.student.class ?? '').trim() || report.student.school,
    current ? `Latest benchmark ${current.label}` : 'No benchmark yet',
    pages === null ? '' : `${pages} ${pages === 1 ? 'page' : 'pages'}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function gradeLabel(grade: string): string {
  const trimmed = grade.trim();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed)) return `Grade ${Number(trimmed)}`;
  if (/^0?k$/i.test(trimmed)) return 'Kindergarten';
  return trimmed;
}

export function domainTitle(domain: string): string {
  return domain ? domain[0]!.toUpperCase() + domain.slice(1) : '';
}

// The server joins every roster teacher with ", "; a student with a full
// schedule can carry seven or more, so the header names two and counts the rest.
export function teacherLine(teacher: string): string {
  const names = teacher.split(',').map((name) => name.trim()).filter(Boolean);
  if (names.length === 0) {
    return '';
  }
  const shown = names.slice(0, 2).join(', ');
  return STRINGS.teacher(names.length > 2 ? `${shown} +${names.length - 2} more` : shown);
}

export function studentSubline(student: ReportStudent): string {
  return [gradeLabel(student.grade), student.school, student.homeLanguage ? STRINGS.homeLanguage(student.homeLanguage) : '']
    .filter(Boolean)
    .join(' · ');
}
